import React, { useState, useEffect, useMemo } from "react";
import Avatar from "../../components/ui/Avatar";
import { motion } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import BaseModal from "../../components/modals/BaseModal";
import Toast from "../../components/ui/Toast";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import monthlyReportService from "../../api/monthlyReportService";
import {
  Calendar,
  CalendarRange,
  FileText,
  Eye,
  Plane,
  Briefcase,
  RefreshCw,
  Save,
  CheckCircle,
  Zap,
  Home,
  Clock,
  AlertCircle,
} from "lucide-react";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Mirrors the backend getWeekBoundsInMonth logic:
 * Week N covers days ((N-1)*7 + 1) through min(N*7, lastDay).
 * Returns { start: Date, end: Date, label: "Mon D – Mon D" } or null when out of range.
 */
const getWeekDateRange = (year, month, week) => {
  const lastDay = new Date(year, month, 0).getDate();
  const startDay = (week - 1) * 7 + 1;
  if (startDay > lastDay) return null;
  const endDay = Math.min(startDay + 6, lastDay);
  const start = new Date(year, month - 1, startDay);
  const end = new Date(year, month - 1, endDay);
  const fmt = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return { start, end, label: fmt(start) + " \u2013 " + fmt(end) };
};

const EmployeesMonthlyReport = () => {
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { auth } = useAuth();
  const employee = auth?.employee;
  const isAdminUser = isAdmin(employee);
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [reportSource, setReportSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedWeek]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const result = await monthlyReportService.getReport(
        selectedYear,
        selectedMonth,
        selectedWeek ?? undefined,
      );
      setReportData(Array.isArray(result.data) ? result.data : []);
      setReportSource(result.source || "computed");
      if (selectedWeek == null) {
        const check = await monthlyReportService.checkSnapshot(
          selectedYear,
          selectedMonth,
        );
        setHasSnapshot(check.hasSnapshot);
      } else {
        setHasSnapshot(false);
      }
    } catch (error) {
      console.error("Failed to fetch report:", error);
      showError("Failed to load monthly report");
      setReportData([]);
      setReportSource(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSnapshot = async () => {
    try {
      setSavingSnapshot(true);
      await monthlyReportService.generateSnapshot(selectedYear, selectedMonth);
      setHasSnapshot(true);
      setReportSource("snapshot");
      await fetchReport();
      showSuccess("Snapshot saved");
    } catch (error) {
      console.error("Failed to save snapshot:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to save snapshot";
      showError(message);
    } finally {
      setSavingSnapshot(false);
    }
  };

  const formatDateWithWeekday = (dateString) => {
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const formatted = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${weekday} - ${formatted}`;
  };

  // Compute date ranges for all 5 possible weeks whenever month/year changes
  const weekDateRanges = useMemo(() => {
    const ranges = {};
    for (let w = 1; w <= 5; w++) {
      ranges[w] = getWeekDateRange(selectedYear, selectedMonth, w);
    }
    return ranges;
  }, [selectedYear, selectedMonth]);

  const reportTitle = useMemo(() => {
    const monthName = MONTHS[selectedMonth - 1];
    if (selectedWeek != null) {
      const range = weekDateRanges[selectedWeek];
      const rangeStr = range ? " (" + range.label + ")" : "";
      return (
        monthName +
        " " +
        selectedYear +
        " \u2014 Week " +
        selectedWeek +
        rangeStr
      );
    }
    return monthName + " " + selectedYear;
  }, [selectedYear, selectedMonth, selectedWeek, weekDateRanges]);

  const isCurrentMonth = useMemo(() => {
    const today = new Date();
    return (
      selectedYear === today.getFullYear() &&
      selectedMonth === today.getMonth() + 1
    );
  }, [selectedYear, selectedMonth]);

  const canSaveSnapshot =
    isAdminUser &&
    hasSnapshot === false &&
    selectedWeek == null &&
    !isCurrentMonth;

  const goToThisMonth = () => {
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth() + 1);
    setSelectedWeek(null);
  };

  // Get employee details for modal
  const employeeDetailsData = useMemo(() => {
    if (!selectedEmployee) return null;

    return {
      wfhDates: selectedEmployee.wfhDates || [],
      vacationDates: selectedEmployee.vacationDates || [],
      earlyLeaveDates: selectedEmployee.earlyLeaveDates || [],
      latePermissionDates: selectedEmployee.latePermissionDates || [],
      missionDates: selectedEmployee.missionDates || [],
    };
  }, [selectedEmployee]);

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setIsDetailsModalOpen(true);
  };

  const renderDetailsSection = (
    title,
    Icon,
    dates,
    color,
    isDateRange = false,
  ) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <h4 className="font-semibold text-gray-900 dark:text-light-100">
          {title} ({dates.length})
        </h4>
      </div>
      {dates.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dates.map((date, index) => (
            <div
              key={index}
              className="px-4 py-2 rounded-lg bg-gray-50 dark:bg-dark-100 border border-gray-200 dark:border-dark-200 text-sm text-gray-700 dark:text-light-300"
            >
              <Calendar className="w-4 h-4 inline mr-2 text-gray-500" />
              {isDateRange ? (
                <>
                  {formatDateWithWeekday(date.start ?? date.startDate)}
                  {(date.end ?? date.endDate) &&
                    (date.start ?? date.startDate) !==
                      (date.end ?? date.endDate) && (
                      <>
                        <br />
                        <span className="ml-6">
                          to {formatDateWithWeekday(date.end ?? date.endDate)}
                        </span>
                        {date.days && (
                          <span className="ml-2 text-xs text-gray-500">
                            ({date.days} days)
                          </span>
                        )}
                      </>
                    )}
                </>
              ) : (
                formatDateWithWeekday(date)
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-light-500 italic">
          No records
        </p>
      )}
    </div>
  );

  const TableComponent = React.memo(
    ({
      data,
      isReadOnly = false,
      onViewDetails,
      loading: tableLoading = false,
    }) => (
      <>
        {/* Mobile Card View */}
        <div className="block md:hidden space-y-3">
          {tableLoading ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <RefreshCw className="w-8 h-8 text-primary-accent animate-spin" />
              <p className="text-sm text-gray-600 dark:text-light-400">
                Loading data...
              </p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-10">
              <FileText className="w-10 h-10 text-gray-400 dark:text-light-500 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-light-500">
                No data available for this period
              </p>
            </div>
          ) : (
            data.map((employee, index) => (
              <motion.div
                key={employee.fingerprint || employee.name || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white dark:bg-primary-card rounded-xl border border-gray-100 dark:border-dark-200 shadow-sm overflow-hidden ${
                  isReadOnly ? "opacity-60" : ""
                }`}
              >
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar src={employee?.photo} name={employee.name} size="sm" className="!w-9 !h-9" />
                    <span className="font-semibold text-gray-900 dark:text-light-100">
                      {employee.name || "—"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-2 text-center">
                      <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">
                        Mission
                      </p>
                      <p className="text-sm font-bold text-teal-800 dark:text-teal-300">
                        {(employee.missionDates || []).reduce(
                          (sum, m) => sum + (m.days || 1),
                          0,
                        )}{" "}
                        days
                      </p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 text-center">
                      <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        WFH
                      </p>
                      <p className="text-sm font-bold text-blue-800 dark:text-blue-300">
                        {employee.wfh ?? 0} days
                      </p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                        Vacation Taken
                      </p>
                      <p className="text-sm font-bold text-purple-800 dark:text-purple-300">
                        {employee.vacationTaken ?? 0} days
                      </p>
                    </div>
                    <div
                      className={`${
                        (employee.remainingVacation ?? 0) < 10
                          ? "bg-red-50 dark:bg-red-900/20"
                          : "bg-green-50 dark:bg-green-900/20"
                      } rounded-lg p-2 text-center`}
                    >
                      <p
                        className={`text-xs font-medium ${
                          (employee.remainingVacation ?? 0) < 10
                            ? "text-red-600 dark:text-red-400"
                            : "text-green-600 dark:text-green-400"
                        }`}
                      >
                        Remaining Vac.
                      </p>
                      <p
                        className={`text-sm font-bold ${
                          (employee.remainingVacation ?? 0) < 10
                            ? "text-red-800 dark:text-red-300"
                            : "text-green-800 dark:text-green-300"
                        }`}
                      >
                        {employee.remainingVacation ?? 0} days
                      </p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2 text-center">
                      <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                        Early Leave
                      </p>
                      <p className="text-sm font-bold text-orange-800 dark:text-orange-300">
                        {employee.earlyLeave ?? 0}
                      </p>
                    </div>
                    <div
                      className={`${
                        (employee.latePermission ?? 0) > 2
                          ? "bg-red-50 dark:bg-red-900/20"
                          : "bg-gray-50 dark:bg-dark-200"
                      } rounded-lg p-2 text-center`}
                    >
                      <p
                        className={`text-xs font-medium ${
                          (employee.latePermission ?? 0) > 2
                            ? "text-red-600 dark:text-red-400"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                      >
                        Late Perm.
                      </p>
                      <p
                        className={`text-sm font-bold ${
                          (employee.latePermission ?? 0) > 2
                            ? "text-red-800 dark:text-red-300"
                            : "text-gray-800 dark:text-gray-300"
                        }`}
                      >
                        {employee.latePermission ?? 0}
                      </p>
                    </div>
                  </div>
                </div>
                {!isReadOnly && (
                  <div className="px-4 py-2.5 bg-gray-50 dark:bg-dark-400 border-t border-gray-100 dark:border-dark-200">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Eye className="w-4 h-4" />}
                      onClick={() => onViewDetails && onViewDetails(employee)}
                      className="w-full justify-center"
                    >
                      View Details
                    </Button>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block w-full overflow-x-auto scrollbar-thin">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden rounded-xl shadow-lg">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-dark-400 dark:to-dark-300">
                  <tr>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Employee Name
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Mission Count
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Work From Home
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Vacation Taken
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Remaining Vacation
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Early Leave
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Late Permission
                    </th>
                    {!isReadOnly && (
                      <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody
                  className={`bg-white dark:bg-primary-card ${isReadOnly ? "opacity-60" : ""}`}
                >
                  {tableLoading ? (
                    <tr>
                      <td
                        colSpan={isReadOnly ? 7 : 8}
                        className="px-6 py-12 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <RefreshCw className="w-8 h-8 text-primary-accent animate-spin" />
                          <p className="text-gray-600 dark:text-light-400">
                            Loading data...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isReadOnly ? 7 : 8}
                        className="px-6 py-12 text-center"
                      >
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-light-500">
                          No data available for this period
                        </p>
                      </td>
                    </tr>
                  ) : (
                    data.map((employee, index) => (
                      <motion.tr
                        key={employee.fingerprint || employee.name || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`border-b border-gray-100 dark:border-dark-100 ${
                          !isReadOnly
                            ? "hover:bg-gray-50 dark:hover:bg-dark-200/50"
                            : ""
                        } transition-colors`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <Avatar src={employee?.photo} name={employee.name} size="md" />
                            <span className="font-medium text-gray-900 dark:text-light-100">
                              {employee.name || "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                            {(employee.missionDates || []).reduce(
                              (sum, m) => sum + (m.days || 1),
                              0,
                            )}{" "}
                            days
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                            {employee.wfh ?? 0} days
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                            {employee.vacationTaken ?? 0} days
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              (employee.remainingVacation ?? 0) < 10
                                ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                                : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                            }`}
                          >
                            {employee.remainingVacation ?? 0} days
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                            {employee.earlyLeave ?? 0}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              (employee.latePermission ?? 0) > 2
                                ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {employee.latePermission ?? 0}
                          </span>
                        </td>
                        {!isReadOnly && (
                          <td className="px-6 py-4 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="w-4 h-4" />}
                              onClick={() =>
                                onViewDetails && onViewDetails(employee)
                              }
                            >
                              View
                            </Button>
                          </td>
                        )}
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    ),
  );

  // Single employee row for normal user view
  const myReport = reportData[0] || null;

  return (
    <PageWrapper
      title={isAdminUser ? "Employees Monthly Report" : "My Monthly Report"}
      subtitle={
        isAdminUser
          ? "Monthly overview of employee attendance and activity"
          : "Your attendance and activity summary"
      }
      icon={<FileText className="w-6 h-6" />}
    >
      <div className="space-y-6">
        {/* Report period selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border-2 border-gray-200 dark:border-dark-200 bg-white dark:bg-primary-card shadow-sm overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-100 bg-gray-50/50 dark:bg-dark-100/30">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary-accent/10 dark:bg-primary-accent/20">
                  <CalendarRange className="w-5 h-5 text-primary-accent" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-light-100">
                    {isAdminUser ? "Report period" : "Select period"}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-light-500 mt-0.5">
                    {isAdminUser
                      ? "Choose year, month, and optional week"
                      : "View your report for a specific month or week"}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={goToThisMonth}
                disabled={isCurrentMonth}
                title={
                  isCurrentMonth
                    ? "Already viewing the current month"
                    : undefined
                }
                className="shrink-0 border-primary-accent/40 text-primary-accent hover:bg-primary-accent/10"
              >
                This month
              </Button>
            </div>
          </div>
          <div className="p-5 flex flex-wrap items-end gap-6">
            <div className="flex flex-wrap items-end gap-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-light-400 uppercase tracking-wide">
                  Year
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-white dark:bg-dark-100 border-2 border-gray-200 dark:border-dark-200 text-gray-900 dark:text-light-100 font-medium focus:border-primary-accent focus:ring-2 focus:ring-primary-accent/20 min-w-[110px] cursor-pointer"
                >
                  {[
                    now.getFullYear(),
                    now.getFullYear() - 1,
                    now.getFullYear() - 2,
                  ].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-light-400 uppercase tracking-wide">
                  Month
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-white dark:bg-dark-100 border-2 border-gray-200 dark:border-dark-200 text-gray-900 dark:text-light-100 font-medium focus:border-primary-accent focus:ring-2 focus:ring-primary-accent/20 min-w-[150px] cursor-pointer"
                >
                  {MONTHS.map((name, i) => (
                    <option key={name} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-600 dark:text-light-400 uppercase tracking-wide">
                  Week
                </label>
                <select
                  value={selectedWeek ?? ""}
                  onChange={(e) =>
                    setSelectedWeek(
                      e.target.value === "" ? null : Number(e.target.value),
                    )
                  }
                  className="appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-white dark:bg-dark-100 border-2 border-gray-200 dark:border-dark-200 text-gray-900 dark:text-light-100 font-medium focus:border-primary-accent focus:ring-2 focus:ring-primary-accent/20 min-w-[220px] cursor-pointer"
                >
                  <option value="">Full month</option>
                  {[1, 2, 3, 4, 5].map((w) => {
                    const range = weekDateRanges[w];
                    if (!range) return null;
                    return (
                      <option key={w} value={w}>
                        {"Week " + w + " (" + range.label + ")"}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            {canSaveSnapshot && (
              <Button
                variant="primary"
                size="sm"
                icon={
                  savingSnapshot ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )
                }
                onClick={handleSaveSnapshot}
                disabled={savingSnapshot}
                className="shrink-0"
              >
                {savingSnapshot ? "Saving…" : "Save snapshot"}
              </Button>
            )}
          </div>
        </motion.div>

        {/* Normal user: professional card-based "My Report" view */}
        {!isAdminUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Period & source badge */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-gray-900 dark:text-light-100">
                {reportTitle}
              </h2>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-dark-100 text-gray-700 dark:text-light-300">
                {reportSource === "snapshot" ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                    Saved report
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 text-amber-500" />
                    Live data
                  </>
                )}
              </span>
            </div>

            {loading ? (
              <div className="card shadow-lg flex flex-col items-center justify-center py-16">
                <RefreshCw className="w-10 h-10 text-primary-accent animate-spin mb-4" />
                <p className="text-sm text-gray-500 dark:text-light-500">
                  Loading your report...
                </p>
              </div>
            ) : !myReport ? (
              <div className="card shadow-lg text-center py-16">
                <FileText className="w-12 h-12 text-gray-400 dark:text-light-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-light-400 font-medium">
                  No data for this period
                </p>
                <p className="text-sm text-gray-500 dark:text-light-500 mt-1">
                  Your approved requests for this month will appear here.
                </p>
              </div>
            ) : (
              <>
                {/* Summary card */}
                <div className="card shadow-lg overflow-hidden ">
                  <div className="p-6 border-b border-gray-100 dark:border-dark-100 bg-gray-50/50 dark:bg-dark-100/30 rounded-xl">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-accent to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {(myReport.name || " ")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .slice(0, 2) || "—"}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-light-100">
                          {myReport.name || "My Report"}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-light-500">
                          Attendance summary for selected period
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
                      <div className="rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                          <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 uppercase tracking-wide">
                            Mission
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-teal-900 dark:text-teal-100">
                          {(myReport.missionDates || []).reduce(
                            (sum, m) => sum + (m.days || 1),
                            0,
                          )}
                          <span className="text-sm font-normal text-teal-600 dark:text-teal-400 ml-1">
                            days
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                            Work from home
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                          {myReport.wfh ?? 0}
                          <span className="text-sm font-normal text-blue-600 dark:text-blue-400 ml-1">
                            days
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Plane className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                          <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                            Vacation taken
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                          {myReport.vacationTaken ?? 0}
                          <span className="text-sm font-normal text-purple-600 dark:text-purple-400 ml-1">
                            days
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-5 h-5 text-green-600 dark:text-green-400" />
                          <span className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide">
                            Remaining vacation
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                          {myReport.remainingVacation ?? 0}
                          <span className="text-sm font-normal text-green-600 dark:text-green-400 ml-1">
                            days
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                          <span className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">
                            Early leave
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">
                          {myReport.earlyLeave ?? 0}
                          <span className="text-sm font-normal text-orange-600 dark:text-orange-400 ml-1">
                            times
                          </span>
                        </p>
                      </div>
                      <div className="rounded-xl bg-gray-50 dark:bg-dark-100 border border-gray-200 dark:border-dark-200 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-gray-600 dark:text-light-400" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-light-300 uppercase tracking-wide">
                            Late permission
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-light-100">
                          {myReport.latePermission ?? 0}
                          <span className="text-sm font-normal text-gray-500 dark:text-light-500 ml-1">
                            times
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-100 dark:border-dark-100">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<Eye className="w-4 h-4" />}
                        onClick={() => handleViewDetails(myReport)}
                        className="border-primary-accent/40 text-primary-accent hover:bg-primary-accent/10"
                      >
                        View date details
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Admin: full table */}
        {isAdminUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-primary-accent/5 to-orange-100/50 dark:from-primary-accent/10 dark:to-orange-900/20 rounded-xl border-2 border-primary-accent/30 shadow-lg overflow-hidden"
          >
            <div className="px-6 py-4 border-b-2 border-primary-accent/30 bg-gradient-to-r from-primary-accent/5 to-transparent flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-accent rounded-lg">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-light-100">
                    {reportTitle}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-light-400 flex items-center gap-2">
                    {reportSource === "snapshot" ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Saved snapshot (unchanged by future edits)
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 text-amber-500" />
                        Live data (current requests)
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <TableComponent
                data={reportData}
                onViewDetails={handleViewDetails}
                loading={loading}
              />
            </div>
          </motion.div>
        )}
      </div>

      {/* Employee Details Modal */}
      <BaseModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        title={selectedEmployee?.name || "Employee Details"}
        subtitle="Monthly attendance and activity details"
        size="xl"
      >
        {selectedEmployee && employeeDetailsData && (
          <div className="space-y-6 max-h-[60vh] overflow-y-auto scrollbar-thin pr-2">
            {/* Mission Dates */}
            {renderDetailsSection(
              "Mission Dates",
              Briefcase,
              employeeDetailsData.missionDates,
              "text-purple-600 dark:text-purple-400",
              true,
            )}

            {/* Vacation Dates */}
            {renderDetailsSection(
              "Vacation Dates",
              Plane,
              employeeDetailsData.vacationDates,
              "text-green-600 dark:text-green-400",
              true,
            )}

            {/* Work From Home Dates */}
            {renderDetailsSection(
              "Work From Home Days",
              Home,
              employeeDetailsData.wfhDates,
              "text-blue-600 dark:text-blue-400",
              true,
            )}

            {/* Early Leave Dates */}
            {renderDetailsSection(
              "Early Leave Dates",
              Clock,
              employeeDetailsData.earlyLeaveDates,
              "text-orange-600 dark:text-orange-400",
              false,
            )}

            {/* Late Permission Dates */}
            {renderDetailsSection(
              "Late Permission Dates",
              AlertCircle,
              employeeDetailsData.latePermissionDates,
              "text-red-600 dark:text-red-400",
              false,
            )}
          </div>
        )}
      </BaseModal>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </PageWrapper>
  );
};

export default EmployeesMonthlyReport;
